'use client';

import { useCallback, useRef, useState } from 'react';

type UseSwipeOptions = {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
};

type UseSwipeReturn = {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  isSwiping: boolean;
  swipeOffset: number;
};

const DEFAULT_THRESHOLD = 50;

export const useSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = DEFAULT_THRESHOLD,
}: UseSwipeOptions): UseSwipeReturn => {
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const isTrackingRef = useRef(false);
  const isHorizontalRef = useRef<boolean | null>(null);
  const offsetRef = useRef(0);

  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const handleStart = useCallback((clientX: number, clientY: number) => {
    startXRef.current = clientX;
    startYRef.current = clientY;
    isTrackingRef.current = true;
    isHorizontalRef.current = null;
    offsetRef.current = 0;
  }, []);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isTrackingRef.current) return;

    const deltaX = clientX - startXRef.current;
    const deltaY = clientY - startYRef.current;

    if (isHorizontalRef.current === null) {
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        isHorizontalRef.current = Math.abs(deltaX) >= Math.abs(deltaY);
      } else {
        return;
      }
    }

    if (!isHorizontalRef.current) return;

    offsetRef.current = deltaX;
    setIsSwiping(true);
    setSwipeOffset(deltaX);
  }, []);

  const handleEnd = useCallback(() => {
    if (!isTrackingRef.current) return;

    isTrackingRef.current = false;
    const offset = offsetRef.current;
    const wasHorizontal = isHorizontalRef.current;

    setIsSwiping(false);
    setSwipeOffset(0);
    offsetRef.current = 0;
    isHorizontalRef.current = null;

    if (!wasHorizontal) return;

    if (offset < -threshold) {
      onSwipeLeft?.();
    } else if (offset > threshold) {
      onSwipeRight?.();
    }
  }, [threshold, onSwipeLeft, onSwipeRight]);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    },
    [handleStart],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    },
    [handleMove],
  );

  const onTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleStart(e.clientX, e.clientY);
    },
    [handleStart],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    },
    [handleMove],
  );

  const onMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const onMouseLeave = useCallback(() => {
    if (isTrackingRef.current) {
      isTrackingRef.current = false;
      offsetRef.current = 0;
      isHorizontalRef.current = null;
      setIsSwiping(false);
      setSwipeOffset(0);
    }
  }, []);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    isSwiping,
    swipeOffset,
  };
};
